/**
 * Shared Framer Motion variants and transitions.
 *
 * Framer Motion is the ONLY animation library in this app. Keeping the easing
 * curves and durations in one module is what stops fifteen slightly-different
 * fades from accumulating.
 *
 * The house curve is a gentle ease-out — motion should feel like it is settling
 * into place, never bouncing.
 */
import type { Transition, Variants } from "framer-motion";

/**
 * Easing and distance are the hero's, so a section arriving on screen reads as
 * the same hand that drew the elevation.
 *
 * The prototype moved things 26-28px over 0.75s, which on a page of technical
 * drawings looks like the content is being flung into place. Restraint is the
 * point of the aesthetic: 10px, under 0.55s, one curve.
 */
export const EASE = [0.2, 0.7, 0.1, 1] as const;
export const EASE_OUT = [0.2, 0.7, 0.1, 1] as const;

/** The wipe used for rules and underlines — a drafting pen, not a bounce. */
export const EASE_WIPE = [0.65, 0, 0.25, 1] as const;

export const DUR = {
  fast: 0.16,
  base: 0.28,
  slow: 0.55,
} as const;

/** Every reveal travels this far. One number, so nothing drifts. */
export const RISE = 10;

export const spring: Transition = {
  type: "spring",
  stiffness: 320,
  damping: 34,
  mass: 0.9,
};

export const softSpring: Transition = {
  type: "spring",
  stiffness: 180,
  damping: 26,
  mass: 1,
};

export const ease: Transition = { duration: DUR.base, ease: EASE };
export const easeOut: Transition = { duration: DUR.slow, ease: EASE_OUT };

/* ------------------------------------------------------------ page level -- */
export const pageVariants: Variants = {
  initial: { opacity: 0, y: RISE },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_OUT } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.18, ease: EASE } },
};

/* --------------------------------------------------------------- reveals -- */
export const revealVariants: Variants = {
  hidden: { opacity: 0, y: RISE },
  visible: { opacity: 1, y: 0, transition: { duration: DUR.slow, ease: EASE_OUT } },
};

export const revealFade: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DUR.slow, ease: EASE_OUT } },
};

export const revealLeft: Variants = {
  hidden: { opacity: 0, x: -RISE },
  visible: { opacity: 1, x: 0, transition: { duration: DUR.slow, ease: EASE_OUT } },
};

export const revealRight: Variants = {
  hidden: { opacity: 0, x: RISE },
  visible: { opacity: 1, x: 0, transition: { duration: DUR.slow, ease: EASE_OUT } },
};

export const revealScale: Variants = {
  // Was a scale-in. Nothing on a drafting sheet grows as it arrives, so this
  // is the same rise as everything else — kept as a separate export only so
  // existing call sites do not have to change.
  hidden: { opacity: 0, y: RISE },
  visible: { opacity: 1, y: 0, transition: { duration: DUR.slow, ease: EASE_OUT } },
};

export const revealPresets = {
  up: revealVariants,
  fade: revealFade,
  left: revealLeft,
  right: revealRight,
  scale: revealScale,
} as const;

export type RevealPreset = keyof typeof revealPresets;

/* ---------------------------------------------------------- stagger sets -- */
export const staggerParent = (stagger = 0.05, delayChildren = 0): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren } },
});

export const staggerChild: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } },
};

/* -------------------------------------------------------------- overlays -- */
export const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22, ease: EASE } },
  exit: { opacity: 0, transition: { duration: 0.18, ease: EASE } },
};

export const modalVariants: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: spring },
  exit: { opacity: 0, y: 10, scale: 0.99, transition: { duration: 0.18, ease: EASE } },
};

export const drawerVariants: Variants = {
  hidden: { x: "100%" },
  visible: { x: 0, transition: { type: "spring", stiffness: 260, damping: 32 } },
  exit: { x: "100%", transition: { duration: 0.26, ease: EASE } },
};

export const mobileMenuVariants: Variants = {
  hidden: { y: "-100%" },
  visible: { y: 0, transition: { type: "spring", stiffness: 220, damping: 30 } },
  exit: { y: "-100%", transition: { duration: 0.32, ease: EASE } },
};

export const dropdownVariants: Variants = {
  hidden: { opacity: 0, y: -8, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: EASE_OUT } },
  exit: { opacity: 0, y: -6, scale: 0.99, transition: { duration: 0.14, ease: EASE } },
};

export const toastVariants: Variants = {
  hidden: { opacity: 0, y: 16, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: spring },
  exit: { opacity: 0, y: 8, scale: 0.98, transition: { duration: 0.2, ease: EASE } },
};

/* ---------------------------------------------------------- micro-motion -- */
export const hoverLift = {
  whileHover: { y: -5, transition: { duration: 0.24, ease: EASE } },
  whileTap: { y: -1, transition: { duration: 0.1 } },
} as const;

export const hoverNudge = {
  whileHover: { y: -2, transition: { duration: 0.2, ease: EASE } },
  whileTap: { scale: 0.98, transition: { duration: 0.1 } },
} as const;

/** Collapse/expand without measuring — height:auto is animatable in Motion. */
export const collapseVariants: Variants = {
  collapsed: { height: 0, opacity: 0, transition: { duration: 0.26, ease: EASE } },
  expanded: { height: "auto", opacity: 1, transition: { duration: 0.32, ease: EASE_OUT } },
};
